import { Warning as WarningIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useCallback } from "react";

interface DeleteBranchModalProps {
  readonly open: boolean;
  readonly branchAddress: string;
  readonly staffCount: number;
  readonly totalBranches: number;
  readonly isDeleting: boolean;
  readonly onClose: () => void;
  readonly onDelete: () => Promise<void>;
}

function DeleteBranchModal({
  open,
  branchAddress,
  staffCount,
  totalBranches,
  isDeleting,
  onClose,
  onDelete,
}: DeleteBranchModalProps) {
  const canDelete = staffCount === 0 && totalBranches > 1;

  const handleDelete = useCallback(async () => {
    if (!canDelete) return;
    await onDelete();
  }, [canDelete, onDelete]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box className="flex items-center gap-2">
          <WarningIcon color="warning" />
          Delete Branch
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className="space-y-4">
          <Typography variant="body1">
            Are you sure you want to delete the branch at:
          </Typography>
          <Typography variant="body1" className="font-semibold">
            {branchAddress}
          </Typography>

          {/* Validation Messages */}
          {staffCount > 0 && (
            <Alert severity="error">
              Cannot delete this branch. There {staffCount === 1 ? "is" : "are"}{" "}
              <strong>{staffCount}</strong> staff member
              {staffCount > 1 ? "s" : ""} assigned to this branch.
            </Alert>
          )}

          {totalBranches <= 1 && (
            <Alert severity="error">
              Cannot delete this branch. A practice must have at least one
              branch.
            </Alert>
          )}

          {canDelete && (
            <Alert severity="warning">
              This action cannot be undone. The branch will be permanently
              deleted.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={!canDelete || isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : null}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteBranchModal;
